import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AppState } from '../../../store';
import { Store } from '@ngrx/store';
import { ShipmentsData } from './shipments.data';
import {
    selectShipmentsCurrentView,
    selectShipmentsTableColumns,
    selectShipmentsTablePage,
    selectShipmentsTableSelection
} from '../../../store/shipments-table/shipments-table.state';
import {
    shipmentTableAddAssist,
    shipmentTableCreateView,
    shipmentTableDeleteView,
    shipmentTableRequest,
    shipmentTableSetColumns,
} from '../../../store/shipments-table/shipments-table.actions';
import { combineLatest, fromEvent, merge, Observable, of, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith, tap } from 'rxjs/operators';
import { ShipmentService } from '../../../data/shipment.service';
import { QuotingService } from '../../shared/services/quoting.service';
import { ShipmentHeader } from '../../../models/shipment-header';
import { SelectionModel } from '@angular/cdk/collections';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ShipmentState } from '../../../utils/shipment-state';
import { selectJournalBusyShipments } from '../../../store/journal/journal.state';
import { DocumentService } from '../../shared/services/document.service';
import { selectDocumentState } from '../../../store/interface/interface.state';
import { DocumentStateEnum } from '../../../enum/document-state.enum';
import { TableColumnService } from '../../shared/services/table-column.service';
import { SubmissionService } from '../../shared/services/submission.service';
import { ShipmentStatusEnum } from '../../../enum/shipment-status.enum';
import { ConfirmService } from '../../shared/services/confirm.service';
import { ShipmentTableActionsComponent } from '../shipment-table-actions/shipment-table-actions.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ShipmentSelectionService } from '../../shared/services/shipment-selection.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ColumnSelectorComponent } from '../../shared/column-selector/column-selector.component';
import { CreateViewComponent } from '../../shared/create-view/create-view.component';
import { selectSetting, selectSettingEditable } from '../../../store/profile/profile.state';
import { SettingEnum } from '../../../enum/setting.enum';
import { interfaceClearExports, interfaceSetExports } from '../../../store/interface/interface.actions';
import { ExportFormatEnum } from '../../../enum/export-format.enum';
import { ExportProviderEnum } from '../../../enum/export-provider.enum';
import { EntityEnum } from '../../../enum/entity.enum';
import { ShipmentTableService } from '../../shared/tables/shipment-table.service';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { ShipmentPreparationSummaryComponent } from '../shipment-preparation-summary/shipment-preparation-summary.component';
import { IconComponent } from '../../shared/icon/icon.component';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { ShipmentIndicatorComponent } from '../shipment-indicator/shipment-indicator.component';
import { RouterLink } from '@angular/router';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';
import { CourierLogoComponent } from '../courier-logo/courier-logo.component';

@Component({
    selector: 'app-shipment-table',
    templateUrl: './shipment-table.component.html',
    styleUrls: ['./shipment-table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    imports: [ MatProgressSpinner, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCheckbox, MatCellDef, MatCell, MatSortHeader, MatButton, RouterLink, ShipmentIndicatorComponent, MatIconButton, MatMenuTrigger, IconComponent, MatMenu, MatMenuItem, ShipmentPreparationSummaryComponent, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator, AsyncPipe, CourierLogoComponent ]
})
export class ShipmentTableComponent implements OnInit, OnDestroy {

    shipmentStates = ShipmentStatusEnum;

    displayColumns: string[];
    currentPageSelectedEntries: ShipmentHeader[] = [];
    currentView: string;
    currentViewCaption: string;

    constructor(private store: Store<AppState>, private shipmentService: ShipmentService, private quotingService: QuotingService,
                private submissionService: SubmissionService, private confirmService: ConfirmService,
                private documentService: DocumentService, private tableColumnService: TableColumnService,
                private bottomSheet: MatBottomSheet, private shipmentSelectionService: ShipmentSelectionService,
                private breakpoints: BreakpointObserver, private dialog: MatDialog, public table: ShipmentTableService) {
    }

    selectionButtonCount$ = this.shipmentSelectionService.selectionButtonCount$;
    selectionButtonSpace$: Observable<number> = this.breakpoints.observe([ Breakpoints.Small, Breakpoints.XSmall ]).pipe(
        map(() => this.breakpoints.isMatched([ Breakpoints.XSmall, Breakpoints.Small ]) ? 1 : 4)
    );
    selectionButtonDisplay$: Observable<boolean> = combineLatest([ this.selectionButtonCount$, this.selectionButtonSpace$ ]).pipe(
        map(([ count, space ]) => count > space)
    );
    shipButton$: Observable<boolean> = combineLatest([
        this.shipmentSelectionService.shipSelectionButton$,
        this.selectionButtonDisplay$
    ]).pipe(
        map(([ ship, selection ]) => ship && !selection)
    );
    quoteButton$: Observable<boolean> = combineLatest([
        this.shipmentSelectionService.quoteSelectionButton$,
        this.selectionButtonDisplay$
    ]).pipe(
        map(([ ship, selection ]) => ship && !selection)
    );
    voidButton$: Observable<boolean> = combineLatest([
        this.shipmentSelectionService.voidSelectionButton$,
        this.selectionButtonDisplay$
    ]).pipe(
        map(([ ship, selection ]) => ship && !selection)
    );
    printButton$: Observable<boolean> = combineLatest([
        this.shipmentSelectionService.printSelectionButton$,
        this.selectionButtonDisplay$
    ]).pipe(
        map(([ ship, selection ]) => ship && !selection)
    );
    pickupButton$: Observable<boolean> = combineLatest([
        this.shipmentSelectionService.pickupSelectionButton$,
        this.selectionButtonDisplay$
    ]).pipe(
        map(([ button, selection ]) => button && !selection)
    );

    currentPageSelectedEntries$: Observable<ShipmentHeader[]> = combineLatest([
        this.store.select(selectShipmentsTableSelection),
        this.store.select(selectShipmentsTablePage)
    ]).pipe(
        map(([ selection, page ]) => page.filter(h => selection.indexOf(h.id) !== -1)),
        tap(headers => this.currentPageSelectedEntries = [ ...headers ])
    );
    selectedQuotableCount$: Observable<number> = this.currentPageSelectedEntries$.pipe(
        map(headers => headers.filter(h => ShipmentState.isQuotable(h.status))),
        map(headers => headers.length)
    );
    selectedPrintableCount$: Observable<number> = this.currentPageSelectedEntries$.pipe(
        map(headers => headers.filter(h => ShipmentState.isSubmitted(h.status))),
        map(headers => headers.length)
    );
    selectedVoidableCount$: Observable<number> = this.currentPageSelectedEntries$.pipe(
        map(headers => headers.filter(h => ShipmentState.isVoidable(h.status))),
        map(headers => headers.length)
    );
    selectedShippableCount$: Observable<number> = this.currentPageSelectedEntries$.pipe(
        map(headers => headers.filter(h => h.status === ShipmentStatusEnum.QUOTED && !!h.serviceId)),
        map(headers => headers.length)
    );
    selectedPickupCount$: Observable<number> = this.currentPageSelectedEntries$.pipe(
        map(headers => headers.filter(h => h.pickupRequired && h.status === ShipmentStatusEnum.MANIFESTED)),
        map(headers => headers.length)
    );
    documentsBusy$: Observable<boolean> = this.store.select(selectDocumentState).pipe(
        map((s) => s !== DocumentStateEnum.NONE)
    );
    busyIds$: Observable<string[]> = this.store.select(selectJournalBusyShipments);

    displayColumns$: Observable<string[]> = this.store.select(selectShipmentsTableColumns).pipe(
        tap(columns => this.displayColumns = columns),
        map((columns) => [ 'select', 'id', ...columns, 'focus' ])
    );

    displayConfigureMenu$ = of(false);

    canSaveView$: Observable<boolean> = combineLatest([
        this.store.select(selectShipmentsCurrentView),
        this.store.select(selectSettingEditable(SettingEnum.USER_SHIPMENT_VIEWS))
    ]).pipe(
        tap(([ name, ]) => console.log('Current View: ' + name)),
        map(([ name, editable ]) => editable && name === null)
    );

    canDeleteView$: Observable<boolean> = combineLatest([
        this.store.select(selectShipmentsCurrentView),
        this.store.select(selectSettingEditable(SettingEnum.USER_SHIPMENT_VIEWS)),
        this.store.select(selectSetting(SettingEnum.USER_SHIPMENT_VIEWS))
    ]).pipe(
        tap(([ name ]) => this.currentView = name),
        tap(([ name, , views ]) => this.currentViewCaption = views ? views.find(v => v.id === name)?.caption || null : null),
        map(([ name, editable, views ]) => views && !!name && editable && views && !!views.find(v => v.id === name))
    );

    dataSource = new ShipmentsData(this.table.currentPageEntries$);
    focus = new SelectionModel<string>(false, [], true);

    ngOnInit(): void {
        this.store.dispatch(shipmentTableRequest());
        this.focus.changed.pipe(
            distinctUntilChanged(),
            filter(() => this.focus.hasValue())
        ).subscribe(() => this.shipmentService.getByKey(this.focus.selected[0]));
        this.store.dispatch(interfaceSetExports({
            formats: [ ExportFormatEnum.CSV, ExportFormatEnum.XLS, ExportFormatEnum.XLSX ],
            provider: ExportProviderEnum.SHIPMENT_TABLE
        }));
    }

    ngOnDestroy(): void {
        this.store.dispatch(interfaceClearExports());
    }

    quote(): void {
        this.shipmentSelectionService.quoteSelection();
    }

    printWaybills(): void {
        this.shipmentSelectionService.printSelection();
    }

    voidShipment(): void {
        this.shipmentSelectionService.voidSelection();
    }

    ship(): void {
        this.shipmentSelectionService.submitSelection();
    }

    pickup(): void {
        this.shipmentSelectionService.pickupSelection();
    }

    selection(): void {
        this.bottomSheet.open(ShipmentTableActionsComponent);
    }

    configureColumns(): void {
        this.dialog
            .open(ColumnSelectorComponent, { data: { entity: EntityEnum.SHIPMENT_HEADER, selection: this.displayColumns } })
            .afterClosed()
            .pipe(
                filter(columns => !!columns),
                tap(columns => this.store.dispatch(shipmentTableSetColumns({ columns })))
            ).subscribe();
    }

    createView(): void {
        this.dialog
            .open(CreateViewComponent)
            .afterClosed()
            .pipe(
                filter(name => !!name),
                tap(name => this.store.dispatch(shipmentTableCreateView({ name })))
            ).subscribe();
    }

    deleteView(): void {
        const name = this.currentViewCaption;
        this.confirmService.confirm($localize`Delete "${name}"`, $localize`Are you sure you wish to delete this view?`,
            () => this.store.dispatch(shipmentTableDeleteView({ id: this.currentView })));
    }

    statusFilter(s: ShipmentStatusEnum): void {
        this.store.dispatch(shipmentTableAddAssist({ id: 'SHIPMENT_STATUS', parameters: s }));
    }
}
